// TSK-5406: Logout on the Select-a-Till screen (Till.js).
// One routine signs the cashier out from every logout on that screen. It never unlinks the till,
// never touches db.tillRegistrations, db.orders, tillEvents or paymentsData, and never resets the
// till session, lastUpdatedTime or the bill counters: the same user signs in again and resumes the
// same session and counter with no Full Sync.
// Whether this user has an open till is read from the server (getTills row of the till this device
// is linked to, and who holds it) together with the local open session. Close Till is offered only
// when both agree.
import { clearAuthCookies } from "../services/authInterceptor";
import useAuthStore from "../stores/useAuthStore";
import upsertPOSLog from "../components/PointOfSale/Retail/posLog";
import { isPastBusinessDay } from "./tillBusinessDate";
import { isPosConfigLoaded, isWaiterUser } from "./tillOpenGate";
import { classifyTillHold, countUnsentOrders, heldRegistration, isEstimateOnlyTill } from "./tillDeviceRegistration";
import { parseTillAccess, sessionOwnerMatchesCurrentUser } from "../constants/accessControl";

export const TILL_LOGOUT_STATE = {
  NONE: "none",
  OPEN_TODAY: "openToday",
  OPEN_PAST_DAY: "openPastDay",
  WAITER: "waiter",
  PRD: "prd",
  ESTIMATE: "estimate",
  MOVED: "moved",
  OFFLINE: "offline",
  OTHER_USER: "otherUser",
};

export const OTHER_USER_MESSAGE = "The open till session was opened by another user. That user closes it.";

export const OFFLINE_MESSAGE = "Till status could not be checked. Close Till after signing in again.";
export const MOVED_MESSAGE = "This till is now used on another device. Close Till there.";
export const RESTAURANT_MESSAGE = "Close Till from the tables screen after signing in again.";
export const CLOSE_CHECK_FAILED_MESSAGE = "Parked bills could not be checked, so Close Till is not available. You can still log out.";

export const unsentBillsMessage = (count) =>
  `${count} bill${count === 1 ? "" : "s"} not yet sent to the server. They stay on this device and are sent after the next login.`;

// Same till: by id when both sides have one, else by search key. Never by name alone (till names
// repeat across stores, BUG-2368).
const sameTill = (cwrTill, tillValue) => {
  if (!cwrTill || !tillValue) return false;
  if (cwrTill.cwrTillID && tillValue.cwr_till_id) return cwrTill.cwrTillID === tillValue.cwr_till_id;
  if (cwrTill.searchKey && tillValue.search_key) return cwrTill.searchKey === tillValue.search_key;
  return false;
};

// tillData.tillAccess is the list from tillData on this screen, or one till once narrowed.
export const findTillAccessEntry = (tillData, tillValue) => {
  const access = tillData?.tillAccess;
  if (!access) return null;
  const list = Array.isArray(access) ? access : [access];
  return list.find((entry) => sameTill(entry?.cwrTill, tillValue)) || null;
};

export const narrowTillData = (tillData, entry) => (entry ? { ...tillData, tillAccess: entry } : null);

// The getTills row of this till (getTills names the id cwrTillID and the key searchKey).
export const findServerTill = (tills, tillValue) =>
  (Array.isArray(tills) ? tills : []).find((row) => sameTill({ cwrTillID: row?.cwrTillID, searchKey: row?.searchKey }, tillValue)) || null;

// Retail layouts are 1 (retail), 3 (kiosk), 4 (market); every other layout is a restaurant screen.
export const isRestaurantTill = (tillData) => {
  const layout = parseInt(parseTillAccess(tillData)?.layout ?? 1, 10);
  return !Number.isNaN(layout) && ![1, 3, 4].includes(layout);
};

// Close Till starts on /close-till, as the billing screen's Logout (useSuperMarketHandlers
// handleLogout) and the Till screen's day-change check open it: that screen works out the
// session's payment totals (allPaymentsData) and moves on to /close-till-all or /cash-to-keep
// for the template. /cash-to-keep opened first has no totals and sends the cashier back to /till.
// A past day closes even on a template with no shift close. null: no Close Till for this till.
export const CLOSE_TILL_START = "/close-till";
export const closeTillStartPath = (posConfig, pastDay) => (posConfig?.shiftClose === "Y" || pastDay ? CLOSE_TILL_START : null);

/**
 * Pure decision. Inputs are read at the moment of the click.
 *   server: { ok: boolean, row: getTills row of this till or null }
 *   heldId / deviceId: this browser's registration id and device id (BUG-2443)
 */
export function resolveTillLogoutState({
  posType,
  posConfig,
  tillValue,
  tillData,
  username,
  currentUser,
  tillDataOwner,
  tillSession,
  openEvent,
  tillMovedAway,
  server,
  heldId,
  deviceId,
}) {
  const entry = findTillAccessEntry(tillData, tillValue);
  const narrowed = narrowTillData(tillData, entry);
  const base = { narrowedTillData: narrowed, closePath: null, reason: null };

  if (posType === "PRD") return { ...base, state: TILL_LOGOUT_STATE.PRD };
  if (narrowed && isWaiterUser(narrowed, username)) return { ...base, state: TILL_LOGOUT_STATE.WAITER };
  if (!isPosConfigLoaded(posConfig)) return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "no_pos_config" };
  if (isEstimateOnlyTill(posConfig)) return { ...base, state: TILL_LOGOUT_STATE.ESTIMATE };

  const localOpen =
    tillSession?.tillStatus === "open" &&
    openEvent?.tillStatus === "open" &&
    !!openEvent?.tillSessionId &&
    openEvent.tillSessionId === tillSession.tillSessionId &&
    sessionOwnerMatchesCurrentUser(tillDataOwner, username || null);
  if (!localOpen) return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "no_local_open_till" };
  // setTillData re-stamps tillDataOwner with whoever is signed in, so the session's opener is read
  // from the open event (OpenTill writes userData.user there). Another user's session is not closed here.
  if (openEvent.user && currentUser && openEvent.user !== currentUser) {
    return { ...base, state: TILL_LOGOUT_STATE.OTHER_USER };
  }

  if (tillMovedAway) return { ...base, state: TILL_LOGOUT_STATE.MOVED };
  if (!server?.ok) return { ...base, state: TILL_LOGOUT_STATE.OFFLINE };
  if (!server.row) return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "till_not_on_server" };

  const hold = classifyTillHold({ activeRows: server.row.tillHistory, heldId, deviceId });
  if (hold.state === "moved") return { ...base, state: TILL_LOGOUT_STATE.MOVED };
  // free: the server holds no registration for the till (released by a Close Till) — nothing to close here.
  if (hold.state === "free") return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "server_till_free" };
  if (!narrowed) return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "no_till_access" };

  const pastDay = isPastBusinessDay(openEvent) && posConfig?.enableAutoStoreClose === "Y";
  const closePath = closeTillStartPath(posConfig, pastDay);
  if (pastDay) return { ...base, state: TILL_LOGOUT_STATE.OPEN_PAST_DAY, closePath };
  if (closePath) return { ...base, state: TILL_LOGOUT_STATE.OPEN_TODAY, closePath };
  return { ...base, state: TILL_LOGOUT_STATE.NONE, reason: "no_shift_close" };
}

/**
 * Everything resolveTillLogoutState needs, read now (never from values captured at mount).
 *   readServerTills(userId) -> getTills list; throws when the server cannot be reached.
 */
export async function readTillLogoutContext({ db, authStore, sessionStore, posConfigStore, username, readServerTills, deviceId }) {
  const { tillData, tillValue, tillDataOwner, userData } = authStore.getState();
  const { tillSession, tillMovedAway } = sessionStore.getState();
  const { posType, posConfig } = posConfigStore.getState();

  let openEvent = null;
  if (tillSession?.tillSessionId) {
    const events = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).toArray();
    openEvent = (events || []).find((e) => e?.tillStatus === "open") || null;
  }

  let server = { ok: false, row: null };
  if (userData?.user_id) {
    try {
      const tills = await readServerTills(userData.user_id);
      server = { ok: Array.isArray(tills), row: findServerTill(tills, tillValue) };
    } catch {
      server = { ok: false, row: null };
    }
  }

  const held = heldRegistration(await db.tillRegistrations.toArray());

  let unsentBills = null;
  try {
    unsentBills = await countUnsentOrders(db);
  } catch (err) {
    console.warn("[TillLogout] unsent bill count failed:", err?.message);
  }

  const decision = resolveTillLogoutState({
    posType,
    posConfig,
    tillValue,
    tillData,
    username,
    currentUser: userData?.user || null,
    tillDataOwner,
    tillSession,
    openEvent,
    tillMovedAway,
    server,
    heldId: held?.heldId || null,
    deviceId,
  });
  return { ...decision, unsentBills, tillSessionId: tillSession?.tillSessionId || null, posConfig };
}

// getTills(userId) answer -> the list; anything else (the service swallows errors) is a failed read.
export async function readServerTillList(getTillsFn, userId) {
  const res = await getTillsFn(userId);
  const list = res?.data?.data?.getTills;
  if (!Array.isArray(list)) throw new Error("getTills returned no till list");
  return list;
}

// Parked bills of this till on the server, read like the billing screen's Logout (this month's
// cartHistory). The service returns undefined on a failure, which is a failed read, not "none".
export async function readServerParkedBills(cartHistoryFn, tillId, now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const res = await cartHistoryFn({
    q: "",
    filter_by: JSON.stringify({ DateRange: [ymd(start), ymd(end)] }),
    sort_by: JSON.stringify({ created: "DESC" }),
    tillId,
  });
  if (res?.status !== 200 || res?.data?.errors?.length) throw new Error("cartHistory could not be read");
  const raw = res?.data?.data?.cartHistory;
  const rows = typeof raw === "string" ? JSON.parse(raw || "[]") : raw || [];
  if (!Array.isArray(rows)) throw new Error("cartHistory returned no list");
  return rows;
}

/**
 * What stops Close Till from this screen. Returns { canClose, messages }.
 *   readParkedBills(tillId) -> server cartHistory rows (saveParkedBills "Y"); throws on failure.
 */
export async function readCloseTillBlockers({ posConfig, narrowedTillData, parkedList, cart, exchangeCart, readParkedBills }) {
  if (isRestaurantTill(narrowedTillData)) return { canClose: false, messages: [RESTAURANT_MESSAGE] };

  const messages = [];
  let parkedCount = 0;
  if (posConfig?.saveParkedBills === "Y") {
    try {
      const rows = await readParkedBills(narrowedTillData?.tillAccess?.cwrTill?.cwrTillID || null);
      parkedCount = (rows || []).filter((row) => row?.cartstatus === "DR").length;
    } catch (err) {
      console.warn("[TillLogout] parked bill check failed:", err?.message);
      return { canClose: false, messages: [CLOSE_CHECK_FAILED_MESSAGE] };
    }
  } else {
    parkedCount = (parkedList || []).length;
  }
  if (parkedCount > 0) messages.push(`${parkedCount} parked bill${parkedCount === 1 ? "" : "s"} on this till. Retrieve or delete them on the billing screen before Close Till.`);

  const cartItems = (cart?.items?.length || 0) + (exchangeCart?.items?.length || 0);
  if (cartItems > 0) messages.push("A bill is still open on the billing screen. Finish or clear it before Close Till.");

  return { canClose: messages.length === 0, messages };
}

// Close Till opened from the Till screen: its Back goes to /till, not to the billing screen the
// cashier never opened. Kept for this browser tab only, for the one till session it was set for.
const CLOSE_FROM_TILL_KEY = "tsk5406CloseTillFrom";

export function markCloseTillFromTillScreen(tillSessionId) {
  try {
    sessionStorage.setItem(CLOSE_FROM_TILL_KEY, JSON.stringify({ from: "/till", tillSessionId }));
  } catch (err) {
    console.warn("[TillLogout] could not keep the Close Till origin:", err?.message);
  }
}

export function clearCloseTillFromTillScreen() {
  try {
    sessionStorage.removeItem(CLOSE_FROM_TILL_KEY);
  } catch {
    // sessionStorage unavailable: nothing was kept
  }
}

// "/till" when this Close Till was opened from the Till screen for the current session, else fallback.
export function closeTillBackPath(fallback, tillSessionId) {
  try {
    const mark = JSON.parse(sessionStorage.getItem(CLOSE_FROM_TILL_KEY) || "null");
    if (mark?.from === "/till" && tillSessionId && mark.tillSessionId === tillSessionId) return "/till";
  } catch {
    // unreadable marker: the screen's own Back
  }
  return fallback;
}

export const LGO_WAIT_MS = 5000;

/**
 * The one sign-out of the Till screen. narrowedTillData: this till's single tillAccess, used only
 * for the LGO log (skipped without one: the screen holds the full till list, not one till).
 */
export async function signOutFromTillScreen({ narrowedTillData, posType, redirectUrl = "/", assign = (url) => window.location.assign(url) }) {
  if (narrowedTillData?.tillAccess?.cwrTill?.cwrTillID) {
    // The LGO request goes out before the login is cleared; a slow server never holds the logout.
    let timer;
    try {
      await Promise.race([
        // No cart here: an empty order number, not the text "undefined" ({}.documentno) in cwr_pos_log.orderno.
        upsertPOSLog({ documentno: "" }, "LGO", undefined, { tillData: narrowedTillData, awaitServer: true }),
        new Promise((resolve) => {
          timer = setTimeout(resolve, LGO_WAIT_MS);
        }),
      ]);
    } catch (err) {
      console.warn("[TillLogout] LGO log failed:", err?.message);
    } finally {
      clearTimeout(timer);
    }
  }
  clearAuthCookies();
  useAuthStore.getState().logout();
  try {
    // BUG-2026: the shell's logout IPC is the authoritative teardown; await it before navigating.
    await window.electronAPI?.auth?.logout();
  } catch (err) {
    console.error("[TillLogout] shell logout failed:", err);
  }
  assign(posType === "PRD" ? "/" : `${redirectUrl || "/"}sign-in`);
}
